import { Injectable } from "@angular/core";
import Decimal from "decimal.js";
import { Currency, CurrencyId } from "../models/currency";
import { currencyOptions } from "../constant/currency";

type ExchangeRateConfig = {
  rate: string;
  locale: string;
  symbol: string;
};

const EXCHANGE_RATES: Record<CurrencyId, ExchangeRateConfig> = {
  USD: { rate: "1", locale: "en-US", symbol: "$" },
  EUR: { rate: "0.854043", locale: "de-DE", symbol: "€" },
  GBP: { rate: "0.762776", locale: "en-GB", symbol: "£" },
};

@Injectable({
  providedIn: "root",
})
export class CurrencyConversionService {
  /**
   * Get the locale matching browser language or fallback to provided locale
   */
  private getSupportedLocale(fallback: string): string {
    try {
      const locales = Intl.NumberFormat.supportedLocalesOf([navigator.language]);
      return locales.length > 0 ? locales[0] : fallback;
    } catch {
      return fallback;
    }
  }

  /**
   * Calculate relative exchange rates based on a default currency.
   * All rates are normalized relative to the default currency.
   *
   * Example: If default is EUR, EUR rate becomes 1, and USD/GBP are recalculated relative to EUR.
   */
  private getRelativeExchangeRates(defaultValue: CurrencyId): Record<CurrencyId, ExchangeRateConfig> {
    const exchangeRates: Record<CurrencyId, ExchangeRateConfig> = JSON.parse(JSON.stringify(EXCHANGE_RATES));
    const baseRate = new Decimal(exchangeRates[defaultValue].rate);

    if (defaultValue === "GBP") {
      exchangeRates.USD.rate = new Decimal(exchangeRates.USD.rate).div(baseRate).toString();
      exchangeRates.EUR.rate = new Decimal(exchangeRates.EUR.rate).div(baseRate).toString();
      exchangeRates.GBP.rate = "1";
    } else if (defaultValue === "EUR") {
      exchangeRates.USD.rate = new Decimal(exchangeRates.USD.rate).div(baseRate).toString();
      exchangeRates.GBP.rate = new Decimal(exchangeRates.GBP.rate).div(baseRate).toString();
      exchangeRates.EUR.rate = "1";
    }

    return exchangeRates;
  }

  /**
   * Get all available currencies with proper exchange rates relative to a default currency
   * @param defaultValue The default/base currency for rate calculation (USD, EUR, or GBP)
   */
  public getCurrencies(defaultValue: CurrencyId = "USD"): Currency[] {
    const exchangeRates = this.getRelativeExchangeRates(defaultValue);

    return (Object.entries(exchangeRates) as [CurrencyId, ExchangeRateConfig][]).map(([id, config]) => {
      const locale = this.getSupportedLocale(config.locale);

      return {
        id,
        text: id,
        symbol: config.symbol,
        locale: config.locale,
        exchangeRate: new Decimal(config.rate),
        format: new Intl.NumberFormat(locale, {
          ...currencyOptions,
          currency: id,
        }),
      };
    });
  }

  /**
   * Get a specific currency with exchange rate relative to a default currency
   * @param currencyId The currency to retrieve (USD, EUR, or GBP)
   * @param defaultValue The default/base currency for rate calculation
   */
  public getCurrency(currencyId: CurrencyId, defaultValue: CurrencyId = "USD"): Currency {
    const currencies = this.getCurrencies(defaultValue);
    return currencies.find(({ id }) => id === currencyId) ?? currencies[0];
  }

  /**
   * Convert a numeric value using currency exchange rate
   * @param value The numeric value to convert
   * @param currency The target currency object with exchange rate
   */
  public convertValue(value: number, currency: Currency): number {
    return currency.exchangeRate
      .mul(value || 0)
      .toDecimalPlaces(2)
      .toNumber();
  }

  /**
   * Format a numeric value using a currency object
   * Applies exchange rate conversion and locale-specific formatting
   * @param value The numeric value to format
   * @param currency The currency object for formatting
   */
  public formatValue(value: number, currency: Currency): string {
    const converted = this.convertValue(value, currency);
    return currency.format.format(converted);
  }
}